import { Shield, Factory, Landmark, Crosshair } from "lucide-react";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, ImageReveal, PageTransition,
} from "@/components/site/Animations";
import { SectionHeader } from "@/components/site/SectionHeader";
import { ContactBanner } from "@/components/site/ContactBanner";
import { Separator } from "@/components/ui/separator";

const ENERGY_IMAGE = "https://images.unsplash.com/photo-1509391111737-9b07f052f6b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxlbmVyZ3klMjBwb3dlciUyMGdyaWQlMjBpbmZyYXN0cnVjdHVyZSUyMGluZHVzdHJpYWwlMjBtYW51ZmFjdHVyaW5nJTIwc3RlZWx8ZW58MHx8fHwxNzczMzQ3NDQ3fDA&ixlib=rb-4.1.0&q=85";
const MATERIALS_IMAGE = "https://images.unsplash.com/photo-1632129402289-8a3190ed7a9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxlbmVyZ3klMjBwb3dlciUyMGdyaWQlMjBpbmZyYXN0cnVjdHVyZSUyMGluZHVzdHJpYWwlMjBtYW51ZmFjdHVyaW5nJTIwc3RlZWx8ZW58MHx8fHwxNzczMzQ3NDQ3fDA&ixlib=rb-4.1.0&q=85";
const MANUFACTURING_IMAGE = "https://images.unsplash.com/photo-1661035738644-b102fd4146eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxlbmVyZ3klMjBwb3dlciUyMGdyaWQlMjBpbmZyYXN0cnVjdHVyZSUyMGluZHVzdHJpYWwlMjBtYW51ZmFjdHVyaW5nJTIwc3RlZWx8ZW58MHx8fHwxNzczMzQ3NDQ3fDA&ixlib=rb-4.1.0&q=85";
const DEFENSE_IMAGE = "/defense_procurement.jpg";

const SECTORS = [
  {
    icon: Crosshair,
    title: "Defense Procurement",
    image: DEFENSE_IMAGE,
    overview: "Cortes Industries delivers procurement and supply chain support for defense and mission-critical programs, where precision, compliance, and timelines are essential to operational readiness.",
    secondParagraph: null,
    areas: ["Defense system procurement and supplier qualification", "Secure and controlled supply chain sourcing", "Advanced manufacturing inputs and components", "Program-aligned sourcing for mission-critical platforms", "Lifecycle procurement support for deployed systems"],
    constraints: ["Strict regulatory and compliance requirements (ITAR / export controls)", "Sensitive sourcing with national security considerations", "Fixed procurement timelines tied to program milestones", "Supplier vetting and traceability requirements"],
  },
  {
    icon: Shield,
    title: "Energy & Emerging Applications",
    image: ENERGY_IMAGE,
    overview: "Cortes Industries supports procurement and supply chain execution across energy resilience programs, alternative fuel pathways, and industrial-scale energy output. Our work spans grid-adjacent infrastructure through emerging energy applications where timelines are compressed and requirements carry significant regulatory weight.",
    areas: ["Resilience and grid-adjacent programs", "Alternative fuel pathways and energy transition infrastructure", "Industrial-scale energy output and distribution", "Emerging energy technology procurement and integration"],
    constraints: ["Fixed timelines with cascading schedule risk", "Cross-border regulatory and compliance requirements", "Sensitive sourcing with geopolitical considerations"],
  },
  {
    icon: Factory,
    title: "Critical Materials & Supply Chains",
    image: MATERIALS_IMAGE,
    overview: "From construction commodities to advanced, tech-enabled materials, Cortes Industries navigates restricted and sensitive sourcing environments. We manage the complexity of material procurement where supply constraints, technology restrictions, and geopolitical factors converge.",
    areas: ["Construction commodities and industrial raw materials", "Advanced, tech-enabled materials sourcing", "Restricted and sensitive material supply chains", "Critical mineral and strategic resource procurement"],
    constraints: ["Supply chain security and restricted access requirements", "Technology export controls and compliance", "Multi-jurisdictional sourcing complexity"],
  },
  {
    icon: Landmark,
    title: "Industrial Infrastructure & Manufacturing",
    image: MANUFACTURING_IMAGE,
    overview: "Cortes Industries supports the procurement and coordination of prefabrication, modular systems, rapid-deployment infrastructure, and manufacturing-enabled delivery. We work at the intersection of construction, manufacturing, and program execution to deliver scalable results.",
    areas: ["Prefabrication and modular building systems", "Rapid-deployment infrastructure for time-critical programs", "Manufacturing-enabled delivery and production coordination", "Building materials and construction supply chain management"],
    constraints: ["Accelerated timelines with low tolerance for delay", "Quality compliance and technical inspection requirements", "Multi-site coordination across geographies"],
  },
];

export default function SectorsPage() {
  return (
    <PageTransition>
      <div data-testid="sectors-page">
        <section className="bg-white pt-10 sm:pt-12 lg:pt-16 pb-10">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="Where We Operate"
                title="Sector Focus"
                description="Cortes Industries operates in high-consequence sectors including defense procurement — environments where supply chains carry weight, timelines are fixed, and requirements evolve."
              />
            </FadeIn>
          </div>
        </section>

        <RevealLine />

        <section className="py-14 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="space-y-16" data-testid="sectors-tabs">
              {SECTORS.map((sector, idx) => (
                <div key={sector.title}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <ImageReveal className={`lg:col-span-5 ${idx % 2 === 1 ? 'lg:order-2' : ''}`} delay={0.1}>
                      <div className="aspect-[4/3] rounded-lg overflow-hidden bg-secondary img-zoom">
                        <img src={sector.image} alt={sector.title} className="w-full h-full object-cover" />
                      </div>
                    </ImageReveal>
                    <FadeIn className={`lg:col-span-7 ${idx % 2 === 1 ? 'lg:order-1' : ''}`} delay={0.2}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                          <sector.icon className="w-5 h-5 text-[hsl(41,47%,61%)]" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                          {sector.title}
                        </h3>
                      </div>
                      <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-6">{sector.overview}</p>
                      {sector.secondParagraph && (
                        <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-6">{sector.secondParagraph}</p>
                      )}
                      <div className="mb-6">
                        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-3" style={{ fontFamily: 'var(--font-display)' }}>Focus Areas</p>
                        <ul className="space-y-2">
                          {sector.areas.map((area) => (
                            <li key={area} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(41,47%,61%)] mt-2 shrink-0" />
                              <p className="text-sm leading-relaxed text-muted-foreground">{area}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4 bg-secondary/60 rounded-lg border border-border">
                        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-2" style={{ fontFamily: 'var(--font-display)' }}>Operating Constraints</p>
                        <ul className="space-y-1.5">
                          {sector.constraints.map((c) => (
                            <li key={c} className="flex items-start gap-2">
                              <div className="w-1 h-1 rounded-full bg-muted-foreground mt-2 shrink-0" />
                              <p className="text-xs text-muted-foreground">{c}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </FadeIn>
                  </div>
                  {idx < SECTORS.length - 1 && <Separator className="mt-16" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        <ContactBanner />
      </div>
    </PageTransition>
  );
}
